import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { hasFreeAnonymousHints, getHintAuthMessage } from '../utils/anonymousHints';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { GameShell, useAiutiniModal } from './layout';
import { GameEndSection, LivesDisplay } from './game';
import SnackAlert from './SnackAlert.js';
import { MemoriaSkeleton } from './Skeleton';

import { getCurrentDate, hasGameHistory } from "../util";
import { buildTrackedAuthRedirectUrl, logGameEvent, trackGameHintInsufficientBalance } from "../analytics";
import { getGameConfig, getGameMessage } from "../games";
import { useMemoriaEvents } from "../hooks/games/useMemoriaEvents";
import { useGettoncini } from "../contexts/GettonciniContext";
import useHasEverAuthenticated from "../hooks/useHasEverAuthenticated";
import GettonciniCostChip from './GettonciniCostChip';
import RewardedGettonciniCard from './RewardedGettonciniCard';
import styles from "../styles/Memoria.module.css";
import modalStyles from "../styles/modal.module.css";
import gameStyles from "../styles/GameStyles.module.css";
import Button from './Button';
import AIHelperTrigger from './ai-helper/AIHelperTrigger';
import { usePuzzleInstances } from '../contexts/PuzzleInstancesContext';
import FaIcon from './FaIcon';
import { getDisabledHintCount, getDisabledHintsMessage } from '../utils/aiutiniModal';

const INSUFFICIENT_HINT_BALANCE_MESSAGE = "Gettoncini insufficienti per questo aiutino.";

const canAffordHintCost = ({ cost, balance, isPreview = false, isFree = false }) => {
  if (isPreview || isFree) return true;
  if (typeof cost !== "number" || typeof balance !== "number") return true;
  return balance >= cost;
};

const ENCOURAGEMENTS = ["Ottimo!", "Ben fatto!"];
const WRONGS = ["Non è una coppia.", "Riprova!"];

const MAX_LIVES = 10;

const MiniMemoriaDemo = () => {
  const colors = [
    "#ffcdd2", // light red
    "#c8e6c9", // light green
    "#bbdefb", // light blue
    "#d1c4e9", // light purple
    "#fff9c4", // light yellow
    "#ffe0b2"  // light orange
  ];
  // Create a board with colors in random positions
  const demoBoard = [
    colors[0], colors[3], colors[1],
    colors[4], colors[2], colors[5],
    colors[3], colors[0], colors[4],
    colors[2], colors[5], colors[1]
  ];

  // Extended flip sequence
  const flipSequence = [
    4, 10, 
    6, 9, 
    5, 8, 
    6, 1, 
    9, 4, 
    2, 11, 
    0, 7,  
    3, 10, 
    5, 10,  
    3, 8, 
  ];

  const [flipped, setFlipped] = useState([]); // indices of currently flipped cards
  const [matched, setMatched] = useState([]); // indices of matched cards
  const [step, setStep] = useState(0); // animation step
  const [stopped, setStopped] = useState(false); // stop animation at end

  useEffect(() => {
    let timer;
    if (stopped) {
      // After 3 seconds, reset everything and restart
      timer = setTimeout(() => {
        setFlipped([]);
        setMatched([]);
        setStep(0);
        setStopped(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
    if (step >= flipSequence.length) {
      // Stop animation, leave matched pairs visible, then trigger stopped
      setStopped(true);
    } else if (flipped.length < 2) {
      // Flip next card in the sequence
      timer = setTimeout(() => {
        setFlipped(prev => [...prev, flipSequence[step]]);
        setStep(prev => prev + 1);
      }, 700);
    } else if (flipped.length === 2) {
      const [i1, i2] = flipped;
      const alreadyMatched = matched.includes(i1) || matched.includes(i2);
      if (!alreadyMatched && demoBoard[i1] === demoBoard[i2]) {
        // It's a match: add to matched, keep flipped, then continue
        timer = setTimeout(() => {
          setMatched(prev => Array.from(new Set([...prev, i1, i2])));
          setFlipped([]);
        }, 900);
      } else {
        // Not a match or already matched: flip back after a short delay
        timer = setTimeout(() => {
          setFlipped([]);
        }, 900);
      }
    }
    return () => clearTimeout(timer);
  }, [step, flipped, matched, demoBoard, flipSequence, stopped]);

  const isFlipped = idx => flipped.includes(idx) || matched.includes(idx);

  return (
    <div className={styles.miniMemoriaDemo}>
      <div className={styles.miniMemoriaGrid}>
        {demoBoard.map((color, idx) => (
          <div
            key={idx}
            className={`${styles.miniMemoriaCard} ${isFlipped(idx) ? styles.miniMemoriaCardFlipped : ''}`}
          >
            <div className={styles.miniMemoriaCardInner}>
              <div className={styles.miniMemoriaCardFront}>
                <FaIcon className="fa fa-plane" />
              </div>
              <div
                className={styles.miniMemoriaCardBack}
                style={isFlipped(idx) ? { background: color } : {}}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Instructions = () => {
  return (
    <div className={`${modalStyles.modalContent} ${modalStyles.instructions}`}>
      <p>Allenare la memoria, ricordando parole conosciute o imparando parole nuove!</p>
      <MiniMemoriaDemo />
      <ul>
        <li>Nella griglia ci sono 12 carte che nascondono 12 parole o espressioni italiane.</li>
        <li>Trova le 6 coppie di parole che condividono una relazione: sinonimi, contrari, ambiti lessical-tematici in comune.</li>
        <li>Scopri due carte alla volta.</li>
        <li>Se le due parole formano una coppia valida, rimangono scoperte e assumono un colore di sfondo.</li>
        <li>Se le due parole non formano una coppia, le carte vengono coperte nuovamente.</li>
        <li>Continua finché non trovi tutte le coppie!</li>
        <li>Hai a disposizione 10 tentativi per trovare tutte le coppie!</li>
      </ul>
      <p>Se resti bloccato, puoi usare uno degli aiutini <FaIcon className="fa fa-medkit"  /> a tua disposizione in alto a destra.</p>
    </div>
  );
};

const MemoriaGame = ({
  date,
  gameConfig: providedGameConfig,
  puzzleConfig,
  sessionState,
  solutions,
  status,
  gameOver,
  won,
  justWon,
  justLost = false,
  isLoading,
  sessionError,
  isEventPending,
  dispatchEvent,
  dispatchHintEvent,
  hintAllowance = null,
  refreshHintAllowance = null,
  isSignedIn = false,
  isPreview = false,
  previewSponsorship = null,
  showNavigation = true,
  showGameHeader = true,
  previewEndLabel,
}) => {
  const hasFreeHints = hasFreeAnonymousHints(hintAllowance, 'memoria', isSignedIn);
  const navigate = useNavigate();
  const location = useLocation();
  const hasEverAuthenticated = useHasEverAuthenticated();

  const gameConfig = providedGameConfig || getGameConfig('memoria');
  const effectiveDate = date || "preview";
  const shouldTrack = !isPreview;
  const state = sessionState;

  // Modal state using the shared hook
  const {
    isModalOpen,
    modalTitle,
    modalContent,
    closeModal,
    showModal,
    showAiutiniModal,
    refreshAiutiniModal,
    isAiutiniModalOpen,
  } = useAiutiniModal();

  useEffect(() => {
    if (isAiutiniModalOpen) refreshHintAllowance?.();
  }, [isAiutiniModalOpen, refreshHintAllowance]);

  // Local UI state
  const [flipped, setFlipped] = useState([]); // indices of currently flipped cards
  const [isAnimating, setIsAnimating] = useState(false);
  const [isRevealingAll, setIsRevealingAll] = useState(false);
  const [revealCountdown, setRevealCountdown] = useState(0);
  const [revealedIndices, setRevealedIndices] = useState([]); // Indices revealed during loss animation
  const [animationSolutions, setAnimationSolutions] = useState(null); // Solutions for animation (set when loss animation starts)
  const [isAnimatingLoss, setIsAnimatingLoss] = useState(false);
  const [lossRevealComplete, setLossRevealComplete] = useState(false);
  const snackAlertRef = useRef();
  const aiutiniModalSignatureRef = useRef('');
  const gameplayStatusRef = useRef(null);

  // Loading state for hints
  const [loadingHint, setLoadingHint] = useState(null); // 'reveal_all' | 'reveal_pair' | null
  const [aiutiniError, setAiutiniError] = useState("");
  const [endSectionAnchor, setEndSectionAnchor] = useState(null);

  // Gettoncini context for hint costs display
  const { balance, getHintCost } = useGettoncini();

  // Get puzzle instance ID for AI helper
  const { getPuzzleInstanceId, getPuzzleInstance } = usePuzzleInstances();
  const puzzleInstanceId = getPuzzleInstanceId(gameConfig.id, effectiveDate);
  const sponsorship = previewSponsorship || (isPreview ? null : getPuzzleInstance(gameConfig.id, effectiveDate)?.sponsorship || null);

  // Get Memoria-specific event helpers
  const { submitGuess, revealAll, revealPair } = useMemoriaEvents(dispatchEvent, dispatchHintEvent);

  // Derive state from session
  const lives = state?.lives ?? MAX_LIVES;
  const serverMatched = state?.matched ?? [];
  const serverMatchedColors = state?.matched_colors ?? {};
  const hintsUsed = state?.hints_used ?? { reveal_all: false, reveal_pair: false };

  // Board: flat array of words from puzzle config
  const board = useMemo(() => {
    if (!puzzleConfig?.board) return [];
    return puzzleConfig.board.flat();
  }, [puzzleConfig]);

  // Effective solutions: use hook solutions, or animation solutions during animation
  const effectiveSolutions = solutions || animationSolutions;

  // Combined matched state for display
  // - During animation: only server matched + animation revealed indices (gradual reveal)
  // - After animation / on reload of completed game: all cards matched
  // - In progress: just server matched
  const displayMatched = useMemo(() => {
    // During loss animation, only show what's been revealed so far
    if (isAnimatingLoss) {
      return [...new Set([...serverMatched, ...revealedIndices])];
    }
    // For completed games (not animating), show all cards
    // This handles both: game just ended + animation done, and reloading a completed game
    if (gameOver && effectiveSolutions && board.length === 12) {
      return Array.from({ length: 12 }, (_, i) => i);
    }
    return serverMatched;
  }, [isAnimatingLoss, revealedIndices, gameOver, effectiveSolutions, board.length, serverMatched]);

  const displayMatchedColors = useMemo(() => {
    // Derive colors from solutions when available (animation or completed game)
    if (effectiveSolutions && board.length === 12) {
      const colors = {};
      for (const pair of effectiveSolutions) {
        if (pair.words && pair.color) {
          const idx1 = board.findIndex(w => w.toLowerCase() === pair.words[0]?.toLowerCase());
          const idx2 = board.findIndex(w => w.toLowerCase() === pair.words[1]?.toLowerCase());
          if (idx1 !== -1) colors[idx1] = pair.color;
          if (idx2 !== -1) colors[idx2] = pair.color;
        }
      }
      return colors;
    }
    return serverMatchedColors;
  }, [effectiveSolutions, board, serverMatchedColors]);


  const ERROR_MESSAGES = {
    INVALID_WORDS: "Parole non valide",
    ALREADY_MATCHED: "Queste parole sono già state abbinate",
    GAME_LOAD_ERROR: "Errore nel caricamento del gioco",
    SUBMIT_ERROR: "Errore durante l'invio",
  };

  // Show instructions modal for first-time players
  useEffect(() => {
    if (isPreview) return;
    if (!isLoading && puzzleConfig && !hasGameHistory(gameConfig.id)) {
      showModal({
        title: "Istruzioni",
        content: <Instructions />
      });
      if (shouldTrack) {
        logGameEvent('instructions_viewed', gameConfig.id, effectiveDate, { first_time: true });
      }
    }
  }, [isLoading, puzzleConfig, effectiveDate, gameConfig.id, showModal, isPreview, shouldTrack]);

  // Effect for reveal_all countdown
  useEffect(() => {
    if (!isRevealingAll) return;

    if (revealCountdown > 0) {
      const timer = setTimeout(() => {
        setRevealCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Countdown finished, hide cards again
      setIsRevealingAll(false);
    }
  }, [isRevealingAll, revealCountdown]);

  const handleCardClick = (idx) => {
    if (isAnimating || flipped.includes(idx) || displayMatched.includes(idx) || gameOver || isRevealingAll || isAnimatingLoss) return;
    if (flipped.length === 0) {
      setFlipped([idx]);
    } else if (flipped.length === 1) {
      setFlipped([flipped[0], idx]);
      setIsAnimating(true);
      setTimeout(() => checkPair(flipped[0], idx), 700);
    }
  };

  const checkPair = async (idx1, idx2) => {
    const word1 = board[idx1];
    const word2 = board[idx2];

    const result = await submitGuess(word1, word2);

    if (!result.success) {
      // Handle error
      const errorMsg = ERROR_MESSAGES[result.error] || ERROR_MESSAGES.SUBMIT_ERROR;
      snackAlertRef.current?.showSnackAlert(errorMsg, "error", 1500);
      setFlipped([]);
      setIsAnimating(false);
      return;
    }

    const { valid, lives_remaining, solutions } = result.result;

    if (valid) {
      // Valid pair found - state is updated automatically by hook
      if (shouldTrack) {
        logGameEvent('guess_correct', gameConfig.id, effectiveDate);
        logGameEvent('level_completed', gameConfig.id, effectiveDate, {
          game_progress: (serverMatched.length + 2) / 12
        });
      }

      setFlipped([]);
      const msg = ENCOURAGEMENTS[Math.floor(Math.random() * ENCOURAGEMENTS.length)];
      snackAlertRef.current?.showSnackAlert(msg, "success", 1500);

    } else {
      // Invalid pair
      if (shouldTrack) {
        logGameEvent('guess_incorrect', gameConfig.id, effectiveDate);
      }
      
      const msg = WRONGS[Math.floor(Math.random() * WRONGS.length)];
      snackAlertRef.current?.showSnackAlert(msg, "error", 1500);
      
      setTimeout(() => setFlipped([]), 900);

      // Check if game is now over (lives exhausted)
      if (lives_remaining === 0 && solutions) {
        // Set animation flag IMMEDIATELY to prevent flash of all cards
        setIsAnimatingLoss(true);
        setAnimationSolutions(solutions);
        
        snackAlertRef.current?.showSnackAlert("Partita finita", "error", 2000);
        
        // Animate revealing remaining pairs after a delay
        // Pass current serverMatched to avoid closure issues
        const currentMatched = [...serverMatched];
        setTimeout(() => {
          animateAnswerReveal(solutions, currentMatched);
        }, 2000);
      }
    }

    setIsAnimating(false);
  };

  // Helper to animate revealing answers on loss
  // Note: isAnimatingLoss and animationSolutions are already set before this is called
  const animateAnswerReveal = useCallback(async (solutionsToReveal, currentMatched) => {
    setFlipped([]);

    // Filter to get remaining pairs not yet matched
    const matchedSet = new Set(currentMatched);
    const remainingPairs = solutionsToReveal.filter(pair => {
      const idx1 = board.findIndex(w => w.toLowerCase() === pair.words[0]?.toLowerCase());
      const idx2 = board.findIndex(w => w.toLowerCase() === pair.words[1]?.toLowerCase());
      return idx1 !== -1 && idx2 !== -1 && !matchedSet.has(idx1) && !matchedSet.has(idx2);
    });

    // Reveal pairs one by one with animation delay
    for (let i = 0; i < remainingPairs.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 900));

      const pair = remainingPairs[i];
      const idx1 = board.findIndex(w => w.toLowerCase() === pair.words[0]?.toLowerCase());
      const idx2 = board.findIndex(w => w.toLowerCase() === pair.words[1]?.toLowerCase());

      // Add these indices to the revealed set for animation
      setRevealedIndices(prev => [...prev, idx1, idx2]);
    }

    // Wait a moment for user to see the last revealed pair before transitioning
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsAnimatingLoss(false);
    setLossRevealComplete(true);
  }, [board]);

  // Redirect to auth page for unauthenticated users
  const redirectToAuth = useCallback((allowanceOverride = null) => {
    if (isPreview) return;
    const authUrl = buildTrackedAuthRedirectUrl({
      cta_kind: (hasEverAuthenticated || hintAllowance?.eligible === false) ? 'sign_in' : 'sign_up',
      source_surface: 'game_hints',
      gate_reason: 'hints_requires_auth',
      next_path: `${location.pathname}${location.search || ''}${location.hash || ''}`,
      message: getHintAuthMessage(allowanceOverride?.game_slug === gameConfig.id ? allowanceOverride : hintAllowance),
      is_returning_device: hasEverAuthenticated,
      game_slug: gameConfig?.id || null,
      track_gate_viewed: true,
      trigger: 'hint_click_redirect',
    });
    navigate(authUrl);
  }, [gameConfig?.id, hasEverAuthenticated, location.pathname, location.search, navigate, isPreview, hintAllowance, location.hash]);

  // Get hint costs for display - memoize to avoid re-renders
  const hintCosts = useMemo(() => ({
    reveal_all: getHintCost('memoria', 'reveal_all'),
    reveal_pair: getHintCost('memoria', 'reveal_pair')
  }), [getHintCost]);

  const hintAffordability = useMemo(() => ({
    reveal_all: canAffordHintCost({ cost: hintCosts.reveal_all, balance, isPreview, isFree: hasFreeHints }),
    reveal_pair: canAffordHintCost({ cost: hintCosts.reveal_pair, balance, isPreview, isFree: hasFreeHints }),
  }), [balance, hintCosts.reveal_all, hintCosts.reveal_pair, isPreview, hasFreeHints]);

  const handleRevealAll = async () => {
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!hintAffordability.reveal_all) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_reveal_all',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: hintCosts.reveal_all,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }
    
    setAiutiniError("");
    setLoadingHint('reveal_all');
    try {
      const result = await revealAll(redirectToAuth);

      if (!result.success) {
        if (result.error === 'INSUFFICIENT_BALANCE') {
          setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
        } else if (result.error === 'ALREADY_USED') {
          setAiutiniError("Aiutino gia usato");
        } else if (result.error === 'NOT_AUTHENTICATED') {
          return;
        } else if (result.error !== 'NOT_AUTHENTICATED') {
          setAiutiniError(result.message || "Errore durante la richiesta");
        }
        return;
      }

      // Start the 10-second reveal
      setIsRevealingAll(true);
      setRevealCountdown(result.hint_data?.duration_seconds ?? 10);

      snackAlertRef.current?.showSnackAlert("Aiutino usato!", "success", 2000);
      closeModal();
    } catch (err) {
      console.error('Reveal all hint failed:', err);
      setAiutiniError("Errore durante la richiesta");
    } finally {
      setLoadingHint(null);
    }
  };

  const handleRevealPair = async () => {
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!hintAffordability.reveal_pair) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_reveal_pair',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: hintCosts.reveal_pair,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }
    
    setAiutiniError("");
    setLoadingHint('reveal_pair');
    try {
      const result = await revealPair(redirectToAuth);

      if (!result.success) {
        if (result.error === 'INSUFFICIENT_BALANCE') {
          setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
        } else if (result.error === 'ALREADY_USED') {
          setAiutiniError("Aiutino gia usato");
        } else if (result.error === 'NO_PAIRS_LEFT') {
          setAiutiniError("Tutte le coppie sono gia state scoperte");
        } else if (result.error === 'NOT_AUTHENTICATED') {
          return;
        } else if (result.error !== 'NOT_AUTHENTICATED') {
          setAiutiniError(result.message || "Errore durante la richiesta");
        }
        return;
      }

      // Pair is automatically matched in state by server
      // result.hint_data contains: { pair, color, pairs_found, game_complete }
      snackAlertRef.current?.showSnackAlert("Coppia rivelata!", "success", 2000);
      closeModal();
    } catch (err) {
      console.error('Reveal pair hint failed:', err);
      setAiutiniError("Errore durante la richiesta");
    } finally {
      setLoadingHint(null);
    }
  };

  const handleRevealAllRef = useRef(handleRevealAll);
  const handleRevealPairRef = useRef(handleRevealPair);
  handleRevealAllRef.current = handleRevealAll;
  handleRevealPairRef.current = handleRevealPair;

  // Update Aiutini modal content when loadingHint changes
  useEffect(() => {
    if (!isAiutiniModalOpen) {
      aiutiniModalSignatureRef.current = '';
      setAiutiniError("");
      return;
    }

    const nextSignature = JSON.stringify({
      loadingHint: loadingHint || '',
      aiutiniError,
      revealAllUsed: !!hintsUsed.reveal_all,
      revealPairUsed: !!hintsUsed.reveal_pair,
      disabled: gameOver || isRevealingAll,
      revealAllCost: hintCosts.reveal_all ?? null,
      revealPairCost: hintCosts.reveal_pair ?? null,
      revealAllAffordable: !!hintAffordability.reveal_all,
      revealPairAffordable: !!hintAffordability.reveal_pair,
      isSignedIn,
      hintAllowance,
      hasFreeHints,
      hasEverAuthenticated,
    });

    if (aiutiniModalSignatureRef.current === nextSignature) {
      return;
    }
    aiutiniModalSignatureRef.current = nextSignature;

    refreshAiutiniModal(
      <Aiutini
        revealAllUsed={hintsUsed.reveal_all}
        revealPairUsed={hintsUsed.reveal_pair}
        onRevealAll={() => handleRevealAllRef.current?.()}
        onRevealPair={() => handleRevealPairRef.current?.()}
        disabled={gameOver || isRevealingAll}
        hintCosts={hintCosts}
        hintAffordability={hintAffordability}
        loadingHint={loadingHint}
        errorMessage={aiutiniError}
        isSignedIn={isSignedIn}
        hasFreeHints={hasFreeHints}
        isPreview={isPreview}
        onAuthRedirect={redirectToAuth}
        hasEverAuthenticated={hasEverAuthenticated || hintAllowance?.eligible === false}
        balance={balance}
      />,
      "Aiutini",
    );
  }, [aiutiniError, gameOver, hasEverAuthenticated, hintAffordability, hintCosts, hintsUsed.reveal_all, hintsUsed.reveal_pair, isAiutiniModalOpen, isRevealingAll, isSignedIn, hasFreeHints, loadingHint, redirectToAuth, refreshAiutiniModal, isPreview, hintAllowance]);

  const handleClosePreview = useCallback(() => {
    window.close();
  }, []);

  const isGameplayAdActive = !gameOver;
  const mobileGameplayAd = useMemo(() => ({
    enabled: isGameplayAdActive || !!endSectionAnchor,
    anchors: endSectionAnchor ? [endSectionAnchor] : [gameplayStatusRef],
    placement: `game-shell-${gameConfig.id}-mobile-gameplay-banner`,
    testId: `${gameConfig.id}-mobile-gameplay-banner`,
  }), [endSectionAnchor, gameConfig.id, isGameplayAdActive]);

  // Build header actions
  const headerActions = [
    {
      icon: "fa fa-question-circle",
      title: "Istruzioni",
      onClick: () => {
        if (shouldTrack) {
          logGameEvent('instructions_viewed', gameConfig.id, getCurrentDate(), { first_time: false });
        }
        showModal({
          title: "Istruzioni",
          content: <Instructions />
        });
      }
    },
    {
      icon: "fa fa-medkit",
      title: "Aiutini",
      onClick: () => {
        setAiutiniError("");
        showAiutiniModal(
          <Aiutini
            revealAllUsed={hintsUsed.reveal_all}
            revealPairUsed={hintsUsed.reveal_pair}
            onRevealAll={() => handleRevealAllRef.current?.()}
            onRevealPair={() => handleRevealPairRef.current?.()}
            disabled={gameOver || isRevealingAll}
            hintCosts={hintCosts}
            hintAffordability={hintAffordability}
            loadingHint={loadingHint}
            errorMessage=""
            isSignedIn={isSignedIn}
            hasFreeHints={hasFreeHints}
            onAuthRedirect={redirectToAuth}
            hasEverAuthenticated={hasEverAuthenticated || hintAllowance?.eligible === false}
            balance={balance}
            isPreview={isPreview}
          />,
          "Aiutini",
        );
        if (shouldTrack) {
          logGameEvent('help_viewed', gameConfig.id, effectiveDate);
        }
      }
    }
  ];

  // Determine if we should show the end section (below the board, not replacing it)
  const showEndSection = gameOver && !isAnimatingLoss && (!justLost || lossRevealComplete);
  const aiSelectionEnabled = gameOver && !isPreview;
  const isAIOnboardingReady = showEndSection && !isPreview;

  // Show skeleton while loading
  if (isLoading) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
      >
        <div className={styles.memoriaContainer}>
          <p className={gameStyles.greeting}>
            {getGameMessage(gameConfig.id, 'not_started')}
          </p>
          <MemoriaSkeleton />
        </div>
      </GameShell>
    );
  }

  // Show error state
  if (sessionError && !state) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
      >
        <div className={styles.memoriaContainer}>
          <p className={gameStyles.greeting}>
            {ERROR_MESSAGES.GAME_LOAD_ERROR}
          </p>
        </div>
      </GameShell>
    );
  }

  return (
    <GameShell
      gameConfig={gameConfig}
      actions={headerActions}
      sponsorship={sponsorship}
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      modalTitle={modalTitle}
      modalContent={modalContent}
      key={sessionState?.session_id || effectiveDate}
      resultsPresentationReady={showEndSection}
      showCelebration={justWon}
      isCompleted={gameOver}
      showNavigation={showNavigation}
      showGameHeader={showGameHeader}
      mobileGameplayAd={mobileGameplayAd}
    >
      <SnackAlert ref={snackAlertRef} />
      <div className={styles.memoriaContainer}>
        <AIHelperTrigger
          puzzleInstanceId={puzzleInstanceId}
          gameSlug={gameConfig.id}
          enabled={aiSelectionEnabled}
          onboardingReady={isAIOnboardingReady}
          coachmarkSelector='[data-ai-component="card_word"]'
        >
        {!showEndSection && (
          <p className={gameStyles.greeting}>
            {getGameMessage(gameConfig.id, 'not_started')}<br/>
          </p>
        )}

        {puzzleConfig?.title && (
          <div className={styles.promptStack}>
            <div className={`${styles.promptSplitRow} ${styles.promptSplitRowTop}`}>
              <div className={`${styles.promptSplitLead} ${styles.promptSplitLeadPrimary}`}>
                <FaIcon className={`fa-solid fa-compass ${styles.promptSplitLeadIcon}`} aria-label="Tema" title="Tema" />
              </div>
              <div className={styles.promptSplitText}>
                <b data-ai-component="title">{puzzleConfig.title}</b>
              </div>
            </div>
          </div>
        )}

        {/* Reveal all countdown overlay */}
        {isRevealingAll && (
          <div className={styles.revealCountdown}>
            <span>{revealCountdown}</span>
          </div>
        )}

        {/* Game board - always visible */}
        <div className={styles.memoriaGrid}>
          {board.map((word, idx) => {
            const isCardFlipped = flipped.includes(idx) || displayMatched.includes(idx) || isRevealingAll;
            const isMatched = displayMatched.includes(idx);
            const isCurrentlySubmitting = isEventPending && flipped.includes(idx) && !isMatched;
            const color = displayMatchedColors[idx];
            return (
              <div
                key={idx}
                className={`${styles.memoriaCard}${isCardFlipped ? ' ' + styles.memoriaCardFlipped : ''}${isMatched ? ' ' + styles.memoriaCardMatched : ''}${isRevealingAll ? ' ' + styles.memoriaCardRevealing : ''}${isCurrentlySubmitting ? ' ' + styles.memoriaCardSubmitting : ''}`}
                onClick={() => !isRevealingAll && !gameOver && handleCardClick(idx)}
              >
                <div className={styles.memoriaCardInner}>
                  <div className={styles.memoriaCardFront}>
                    <FaIcon className="fa fa-plane" />
                  </div>
                  <div className={styles.memoriaCardBack} style={isCardFlipped && isMatched && color ? { background: color } : {}}>
                    <span data-ai-component="card_word" data-ai-word={word}>{word}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </AIHelperTrigger>

        {/* Lives display - only during gameplay */}
        {!gameOver && (
          <div ref={gameplayStatusRef}>
            <LivesDisplay lives={lives} maxLives={MAX_LIVES} />
          </div>
        )}

        {/* Game end section - shows below board when game ends */}
        {showEndSection && (
          isPreview && !previewSponsorship ? (
            <div ref={setEndSectionAnchor} className={styles.previewEndSection}>
              <Button primary onClick={handleClosePreview}>
                {previewEndLabel || "Chiudi anteprima"}
              </Button>
            </div>
          ) : (
            <GameEndSection
              ref={setEndSectionAnchor}
              won={won}
              justWon={justWon}
              gameConfig={gameConfig}
              sessionState={sessionState}
              puzzleConfig={puzzleConfig}
              justCompleted={justWon || justLost}
              date={effectiveDate}
              shouldTrack={shouldTrack}
              sponsorship={sponsorship}
              previewMode={isPreview}
              message={won ? 'Hai scoperto tutte le coppie! 🚀' : 'Hai esaurito tutte le vite! 💔'}
              snackAlertRef={snackAlertRef}
            />
          )
        )}
      </div>
    </GameShell>
  );
};

export default MemoriaGame;

const Aiutini = ({ 
  revealAllUsed, 
  revealPairUsed, 
  onRevealAll, 
  onRevealPair, 
  disabled, 
  hintCosts = {},
  hintAffordability = {},
  loadingHint = null,
  errorMessage = "",
  isSignedIn = true,
  hasFreeHints = false,
  onAuthRedirect = null,
  hasEverAuthenticated = false,
  balance = 0,
  isPreview = false,
}) => {
  const renderButtonContent = ({ alreadyUsed = false } = {}) => {
    if (isPreview) {
      return 'Ricevi aiutino';
    }
    if (!isSignedIn && !hasFreeHints) {
      return hasEverAuthenticated ? 'Accedi' : 'Registrati';
    }
    if (alreadyUsed) {
      return 'Già usato';
    }
    return 'Usa aiutino';
  };
  const shouldShowCostChip = (cost) => !isPreview && (hasFreeHints || (Number.isFinite(Number(cost)) && Number(cost) > 0));
  const disabledHintCount = getDisabledHintCount({
    isSignedIn,
    isPreview,
    hintAffordability,
    excludedHintKeys: [
      ...(revealAllUsed ? ['reveal_all'] : []),
      ...(revealPairUsed ? ['reveal_pair'] : []),
    ],
  });
  const shouldShowInsufficientBalanceState = isSignedIn && (
    errorMessage === INSUFFICIENT_HINT_BALANCE_MESSAGE
    || disabledHintCount > 0
  );
  const shouldShowGenericError = Boolean(errorMessage) && !shouldShowInsufficientBalanceState;

  return (
    <div className={modalStyles.modalContent}>
      {shouldShowInsufficientBalanceState && (
        <div className={styles.aiutiniInlineError} role="alert">
          <FaIcon className="fa-solid fa-circle-exclamation" />
          <div className={styles.aiutiniInlineErrorContent}>
            <div>
              {getDisabledHintsMessage({
                balance,
                disabledHintCount: Math.max(disabledHintCount, 1),
              })}
            </div>
            <div className={styles.aiutiniInlineErrorCard}>
              <RewardedGettonciniCard
                sourceSurface="aiutini_modal"
                gameSlug="memoria"
                compact
              />
            </div>
          </div>
        </div>
      )}
      {shouldShowGenericError && (
        <div className={styles.aiutiniInlineError} role="alert">
          <FaIcon className="fa-solid fa-circle-exclamation" />
          <span>
            {errorMessage}
          </span>
        </div>
      )}
      {(!isSignedIn || !shouldShowInsufficientBalanceState) && (
        <p className={styles.aiutinoPreamble}>
          {!isSignedIn && !isPreview && !hasFreeHints ? (
            <>
              Per usare gli aiutini hai bisogno di gettoncini, che puoi guadagnare con un account gratuito.{' '}
              <span className={styles.authLink} onClick={onAuthRedirect}>
                {hasEverAuthenticated ? 'Accedi' : 'Registrati'} <FaIcon className="fa-solid fa-arrow-right"  />
              </span>
            </>
          ) : 'Se sei bloccato, puoi usare uno di questi aiutini. Ogni aiutino può essere usato una sola volta.'}
        </p>
      )}
      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}><FaIcon className="fa-solid fa-eye"  /></span>
            <span className={styles.aiutinoTitle}>Mostra tutto</span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              onClick={onRevealAll}
              disabled={disabled || revealAllUsed || loadingHint !== null || (isSignedIn && !revealAllUsed && !hintAffordability.reveal_all)}
              className={[
                styles.aiutinoActionRight,
                isSignedIn && !revealAllUsed && !hintAffordability.reveal_all ? styles.aiutinoUnavailable : "",
              ].filter(Boolean).join(" ")}
              loading={loadingHint === 'reveal_all'}
              aria-disabled={isSignedIn && !revealAllUsed && !hintAffordability.reveal_all}
            >
              {renderButtonContent({ alreadyUsed: revealAllUsed })}
            </Button>
            {shouldShowCostChip(hintCosts.reveal_all) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.reveal_all} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Mostra tutte le carte per 10 secondi. Non potrai selezionare nessuna carta durante questo tempo.</span>
      </div>

      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}><FaIcon className="fa-solid fa-link"  /></span>
            <span className={styles.aiutinoTitle}>Rivela coppia</span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              onClick={onRevealPair}
              disabled={disabled || revealPairUsed || loadingHint !== null || (isSignedIn && !revealPairUsed && !hintAffordability.reveal_pair)}
              className={[
                styles.aiutinoActionRight,
                isSignedIn && !revealPairUsed && !hintAffordability.reveal_pair ? styles.aiutinoUnavailable : "",
              ].filter(Boolean).join(" ")}
              loading={loadingHint === 'reveal_pair'}
              aria-disabled={isSignedIn && !revealPairUsed && !hintAffordability.reveal_pair}
            >
              {renderButtonContent({ alreadyUsed: revealPairUsed })}
            </Button>
            {shouldShowCostChip(hintCosts.reveal_pair) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.reveal_pair} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Mostra una coppia non ancora scoperta.</span>
      </div>
    </div>
  );
};
